import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState } from 'react';

import AutoComplete from 'shopper/components/AutoComplete';
import Icon from 'shopper/components/Icon';
import SearchInput from 'shopper/components/SearchInput';

import { sendEvent } from 'lib/gtag';
import { toStoreCouponsLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { storeQueryKeys } from 'lib/queryKeys';
import { debounce, noop } from 'lib/utils';

import { getStoreAutocompleteKeywords } from 'services/store';

const AUTOCOMPLETE_INITIALIZATION_LENGTH = 2;
const DEBOUNCE_REFETCH_TIMER = 300;

const StoreWithCouponsSearch = ({
  className,
  gaEventCategory,
  onClick = noop,
}) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');

  const {
    data: itemsToAutoComplete,
    refetch,
    isInitialLoading,
  } = useQuery({
    queryKey: storeQueryKeys.autoCompleteList({
      query: inputValue.slice(0, 3),
    }),
    queryFn: ({ signal }) =>
      getStoreAutocompleteKeywords(
        { query: inputValue.slice(0, 3) },
        { signal }
      ),
    enabled: false,
    cacheTime: 60 * 60 * 60, // 1min
    select: (data) =>
      data.map(({ storeSlug, storeTitle, ...rest }) => ({
        /**
         * TODO:
         * - remove this replace after backend `store_slug` field fix
         */
        value: storeSlug.replace(/_/g, '-'),
        text: storeTitle,
        ...rest,
      })),
  });

  const debouncedRefetch = debounce(() => {
    refetch();
  }, DEBOUNCE_REFETCH_TIMER);

  const onAutoCompleteChange = ({ target: { value } }) => {
    setInputValue(value.toLowerCase());

    if (value.length > AUTOCOMPLETE_INITIALIZATION_LENGTH) {
      debouncedRefetch();
    }
  };

  const onCleanup = () => {
    setInputValue('');
  };

  const onSelectItem = (slug) => {
    onClick();
    router.push(toStoreCouponsLink(slug));
  };

  return (
    <AutoComplete.Root
      className={className}
      data-test-selector="store-with-coupons-autocomplete"
      full
    >
      <AutoComplete.Input
        as={SearchInput}
        iconRight={<Icon name="search" />}
        placeholder={placeholder('inputs.placeholders.searchCouponsStores')}
        value={inputValue}
        onChange={onAutoCompleteChange}
        onCleanup={onCleanup}
        onFocus={() => {
          sendEvent({
            action: 'store_coupon__textbox__click',
            category: gaEventCategory,
          });
        }}
      />
      <AutoComplete.List items={itemsToAutoComplete} loading={isInitialLoading}>
        {(items) =>
          items.map(({ control, key, couponCount }) => (
            <AutoComplete.ListItem
              key={key}
              control={control}
              counter={couponCount}
              data-test-selector="cupons-autocomplete-item"
              onSelect={onSelectItem}
            />
          ))
        }
      </AutoComplete.List>
    </AutoComplete.Root>
  );
};

StoreWithCouponsSearch.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

export default StoreWithCouponsSearch;
